import React, { ReactNode, useState } from 'react';
import { UpgradeModalTrigger, upgradeModalKeysList } from './util';
import useCurrentTeam from '~/react-hooks/use-current-team';
import { billingRoutes } from '~/App/Modules/NewBilling';
import { CheckoutType } from '~/App/Modules/NewBilling/Pages/Checkout';
import { useGlobalEvent } from 'beautiful-react-hooks';
import { trackUpgradeModalView } from '~/App/Services/BillingAnalytics';
import { PlanCard } from '~/types/models/plasmic';
import { StripePaymentModal } from './Modals/StripePaymentModal';
import { getPricePlanSlug } from '~/App/Modules/NewBilling/Hooks/useMappedPlanCards';
import { useProceedCheckoutPlan } from '~/App/Modules/NewBilling/Hooks/useCheckout';
import { queryKeys as subscriptionQueryKeys } from '~/App/Modules/NewBilling/Hooks/useSubscription';
import { queryKeys as creditCardQueryKeys } from '~/App/Modules/NewBilling/Hooks/useCreditCard';
import { queryKeys as invoiceQueryKeys } from '~/App/Modules/NewBilling/Hooks/useInvoiceInfo';
import { useQueryClient } from 'react-query';
import { useCustomerCoupon } from '~/App/Api/Coupon';
import PaymentSuccessModal from './Modals/PaymentSuccessModal';
import { UpgradeModal } from './Modals/UpgradeModal';
import LegacyChangePlanModal from '~/App/Modules/Billing/Components/LegacyChangePlanModal';
import { PaymentGateway } from '~/types/models';
import { openShopifyCheckoutPage } from '~/App/Modules/NewBilling/utils';
import useAnalytics from '~/react-hooks/use-analytics';
import {
  CHECKOUT_COMPLETED,
  CHECKOUT_STARTED,
} from '~/generated/AnalyticsTypes';

export const startUpgradeModalFlowEventName = 'upgrade-modal-flow::start';

export enum FlowModalType {
  Legacy = 'legacy',
  Upgrade = 'upgrade',
  Payment = 'payment',
  Success = 'success',
}

export interface StartUpgradeModalFlowProps {
  trigger: UpgradeModalTrigger;
  withAddOn?: boolean;
  upgradeModalNote?: ReactNode;
  successModalNote?: ReactNode;
  onBeforeRedirect?: () => Promise<void>;
  onPaymentSuccess?: () => void;
  onClose?: () => void;
  reason?: string;
}

export const startUpgradeModalFlow = (props: StartUpgradeModalFlowProps) => {
  const plandalf = window.plandalf;

  plandalf.intel?.action('entry', {
    flow: 'upgrade-button',
    element: props.trigger,
    scenario: props.reason,
  });

  window.dispatchEvent(
    new CustomEvent<StartUpgradeModalFlowProps>(
      startUpgradeModalFlowEventName,
      {
        detail: props,
      }
    )
  );
};

const DEFAULT_VOID_FUNCTION = () => undefined;

export function UpgradeModalFlow() {
  const { data: coupon } = useCustomerCoupon({});
  const queryClient = useQueryClient();
  const analytics = useAnalytics();
  const plandalf = window.plandalf;

  const {
    on_legacy_plan: isOnSingleLegacyPlan,
    has_valid_card_on_file: hasValidCardOnFile,
    payment_gateway: paymentGateway,
  } = useCurrentTeam();

  const startUpgradeModalFlow = useGlobalEvent<
    CustomEvent<StartUpgradeModalFlowProps>
  >(startUpgradeModalFlowEventName as keyof WindowEventMap);

  const [activeModal, setActiveModal] = useState<FlowModalType>();
  const [trigger, setTrigger] = useState<UpgradeModalTrigger>();
  const [reason, setReason] = useState<string>();
  const [amount, setAmount] = useState<number>();
  const [planSlug, setPlanSlug] = useState<string>();
  const [withAddOn, setWithAddOn] = useState<boolean>();
  const [upgradeModalNote, setUpgradeModalNote] = useState<ReactNode>();
  const [successModalNote, setSuccessModalNote] = useState<ReactNode>();
  const [onBeforeRedirect, setOnBeforeRedirect] = useState<
    (() => Promise<void>) | undefined
  >(DEFAULT_VOID_FUNCTION);
  const [onPaymentSuccess, setOnPaymentSuccess] = useState<
    (() => void) | undefined
  >(DEFAULT_VOID_FUNCTION);
  const [onClose, setOnClose] = useState<(() => void) | undefined>(
    DEFAULT_VOID_FUNCTION
  );

  const closeModal = () => {
    setActiveModal(undefined);
    setTrigger(undefined);
    setReason(undefined);
    setAmount(undefined);
    setPlanSlug(undefined);
    setWithAddOn(undefined);
    setUpgradeModalNote(undefined);
    setSuccessModalNote(undefined);
    setOnBeforeRedirect(DEFAULT_VOID_FUNCTION);
    setOnPaymentSuccess(DEFAULT_VOID_FUNCTION);
    setOnClose(DEFAULT_VOID_FUNCTION);

    onClose?.();
  };

  startUpgradeModalFlow((event) => {
    const {
      trigger,
      reason,
      withAddOn,
      upgradeModalNote,
      successModalNote,
      onBeforeRedirect,
      onPaymentSuccess,
      onClose,
    } = event.detail;

    setUpgradeModalNote(upgradeModalNote);
    setSuccessModalNote(successModalNote);
    setTrigger(trigger);
    setReason(reason);
    setWithAddOn(withAddOn);

    if (onBeforeRedirect) {
      setOnBeforeRedirect(() => onBeforeRedirect);
    }

    if (onPaymentSuccess) {
      setOnPaymentSuccess(() => onPaymentSuccess);
    }

    if (onClose) {
      setOnClose(() => onClose);
    }

    if (isOnSingleLegacyPlan) {
      setActiveModal(FlowModalType.Legacy);
    } else {
      const { viewContext } = upgradeModalKeysList[trigger];
      setActiveModal(FlowModalType.Upgrade);
      trackUpgradeModalView(viewContext);
    }
  });

  const { mutateAsync: proceedCheckoutPlan, isLoading: isCheckingOut } =
    useProceedCheckoutPlan({
      options: {
        onSuccess: () => {
          onPaymentSuccess?.();

          plandalf.intel?.action('complete', {
            result: 'transaction_successful',
            id: planSlug,
            scenario: reason,
            element: trigger,
            flow: 'upgrade-button',
          });

          window.dataLayer.push({
            event: 'purchase',
            value: amount,
            currency: 'USD',
            transaction_id: planSlug,
            items: [{ item_name: planSlug, price: amount, quantity: 1 }],
            transactionValue: amount,
           });

          queryClient.invalidateQueries(subscriptionQueryKeys.details());
          queryClient.invalidateQueries(creditCardQueryKeys.detail);
          queryClient.invalidateQueries(invoiceQueryKeys.detail);

          if (trigger) {
            const { viewContext: context } = upgradeModalKeysList[trigger];
            analytics.track(CHECKOUT_COMPLETED, { context });
          }

          setActiveModal(FlowModalType.Success);
        },
      },
    });

  const onSelectPlan = async (planSlug: string, planCard: PlanCard) => {
    if (!trigger) return;

    const { viewContext: context } = upgradeModalKeysList[trigger];
    analytics.track(CHECKOUT_STARTED, { context });

    if (paymentGateway == PaymentGateway.Shopify) {
      openShopifyCheckoutPage(planSlug);
      return;
    }

    if (hasValidCardOnFile) {
      setActiveModal(FlowModalType.Payment);
      setPlanSlug(planSlug);
      setAmount(
        getPricePlanSlug({ planSlug, planCard, coupon: coupon?.coupon })
      );
    } else {
      await onBeforeRedirect?.();

      window.location.href = `${billingRoutes.checkout}${CheckoutType.Plans}?items=${planSlug}&context=${context}`;
    }
  };

  const onPayClick = () => {
    if (!planSlug) return;

    void proceedCheckoutPlan({ planSlug });
  };

  if (!trigger) return <></>;

  return (
    <>
      <LegacyChangePlanModal
        onBeforeRedirect={onBeforeRedirect}
        open={activeModal === FlowModalType.Legacy}
        onModalClose={closeModal}
      />
      <UpgradeModal
        open={activeModal === FlowModalType.Upgrade}
        trigger={trigger}
        onPlanSelect={onSelectPlan}
        onCloseClick={closeModal}
        withAddOn={withAddOn}
        note={upgradeModalNote}
      />
      <StripePaymentModal
        open={activeModal === FlowModalType.Payment}
        onCloseClick={closeModal}
        loading={isCheckingOut}
        amount={amount}
        onSubmitClick={onPayClick}
      />
      <PaymentSuccessModal
        open={activeModal === FlowModalType.Success}
        onCloseClick={() => window.location.reload()}
        onContinueClick={() => window.location.reload()}
        note={successModalNote}
      />
    </>
  );
}
